import { FieldArray } from 'react-final-form-arrays';
import { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { uniqBy } from 'ramda';

import { KnmResultInfoTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResultInfoTypeCodesEnum';
import { IKnmAddressTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmAddressTypeSelectOption';
import { IKnmInspectorTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmInspectorTypeSelectOption';
import { IKnmResultInfoTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmResultInfoTypeSelectOption';
import { KnmAddressField } from 'KnmPackage/MainModule/components/KnmAddressField';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpObjectSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpObjectSelectOption';
import { ResultsOpenedFlagsType } from 'KnmPackage/MainModule/types/ResultsOpenedFlagsType';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { ORGANIZATION_NAME_BLOCK_ID } from 'KnmPackage/MainModule/constants/fieldsElementsIds';

import { IKnmErpFormResult } from '../interfaces/IKnmErpFormResult';
import styles from './KnmErpResult.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

const COLLAPSE_TITLE_BASE = 'Результат';

interface IKnmErpResultProps {
  formRef: IKnmFormRef | undefined;
  id: string;
  index: number;
  knmObjectsOptions: IKnmErpObjectSelectOption[] | undefined;
  knmAddressTypesOptions: IKnmAddressTypeSelectOption[] | undefined;
  knmInspectorTypesSelectOptions: IKnmInspectorTypeSelectOption[] | undefined;
  knmResultInfoTypesSelectOptions: IKnmResultInfoTypeSelectOption[] | undefined;
  name: string;
  objectsResults: IKnmForm['objectsResults'];
  resultsOpenedFlags: ResultsOpenedFlagsType;
  value: IKnmErpFormResult;
  watchOnly: boolean;
  changeResultsOpenedFlags: (id: string, value: boolean) => void;
  onRemove: () => void;
  isUserSupervisor: boolean | undefined;
  isUserProsecutor: boolean | undefined;
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
}

export const KnmErpResult = ({
  formRef,
  id,
  index,
  knmObjectsOptions,
  knmAddressTypesOptions,
  knmInspectorTypesSelectOptions,
  knmResultInfoTypesSelectOptions,
  name,
  objectsResults,
  resultsOpenedFlags,
  value,
  watchOnly,
  changeResultsOpenedFlags,
  onRemove,
  isUserSupervisor,
  isUserProsecutor,
  created,
  settings,
}: IKnmErpResultProps) => {
  const [isTrigger, setIsTrigger] = useState(false);
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const addInspector = useCallback(() => !watchOnly && push && push(`${name}.inspectors`, undefined), [
    name,
    push,
    watchOnly,
  ]);
  const makeRemoveInspectorHandler = (i: number) => () => !watchOnly && remove && remove(`${name}.inspectors`, i);
  const addDelegate = useCallback(() => !watchOnly && push && push(`${name}.delegates`, undefined), [
    name,
    push,
    watchOnly,
  ]);
  const makeRemoveDelegateHandler = (i: number) => () => !watchOnly && remove && remove(`${name}.delegates`, i);
  const addAdditionalInfo = useCallback(() => !watchOnly && push && push(`${name}.additionalInfo`, undefined), [
    name,
    push,
    watchOnly,
  ]);
  const makeRemoveAdditionalInfoHandler = (i: number) => () =>
    !watchOnly && remove && remove(`${name}.additionalInfo`, i);

  const isReplaceAddressTypeFieldOnHazardCheckBox = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ERP_NEW_FIELDS_FZ68_4_1_36,
  );

  const isUse4136 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_36_STARTS_FROM);
  const isUseManualAddressAndFiasRegion = isUse4136 && (isUserProsecutor || isUserSupervisor);

  const { isNew } = value;

  const uniqueKnmObjectsOptions = uniqBy(item => item.id, knmObjectsOptions || []);
  const customKnmObjectsSelectOptions: IKnmErpObjectSelectOption[] | undefined = uniqueKnmObjectsOptions?.map(
    knmObjectSelectOption => ({
      ...knmObjectSelectOption,
      isDisabled: objectsResults?.some(
        objectResult =>
          knmObjectSelectOption.id === objectResult?.object?.id && knmObjectSelectOption.id !== value.object?.id,
      ),
    }),
  );

  const handleResultCollapseToggle = useCallback(() => {
    if (id) {
      const isOpenedFlagValue = resultsOpenedFlags[id];

      changeResultsOpenedFlags(id, !isOpenedFlagValue);
    }
    setTimeout(() => {
      setIsTrigger(!isTrigger);
    });
  }, [changeResultsOpenedFlags, id, isTrigger, resultsOpenedFlags]);

  // Поиск объекта-субъекта по guid среди всех форменных массивов объектов (тот же алгоритм, что был в прежнем useEffect).
  // Возвращает форменный объект, у которого поле isAddressHazard уже нормализовано маппером knmErpSaga.
  const findReferenceObjectByGuid = useCallback(
    (guid: string | null | undefined) => {
      if (!guid) return undefined;
      const objects = formRef?.current.getState().values.objects || [];
      const objectsErknm = formRef?.current.getState().values.objectsErknm || [];
      const objectsErvk = formRef?.current.getState().values.objectsErvk || [];
      const objectsPm = formRef?.current.getState().values.objectsPm || [];

      const allObjects = [...objects, ...objectsErknm, ...objectsErvk, ...objectsPm].filter(obj => !!obj);

      return uniqBy(
        (item: { guid?: string | null } | undefined) => item?.guid,
        allObjects.filter(obj => obj?.guid === guid || obj?.listId === guid) || [],
      )?.[0];
    },
    [formRef],
  );

  const referenceObject = useMemo(() => findReferenceObjectByGuid(value.object?.id || value.object?.guid), [
    findReferenceObjectByGuid,
    value,
  ]);

  const handleObjectChange = useCallback(
    (option?: IKnmErpObjectSelectOption) => {
      const nextReferenceObject = findReferenceObjectByGuid(option?.id || option?.guid) as
        | { isAddressHazard?: boolean }
        | undefined;

      formRef?.current.change(`${name}.isAddressHazard` as keyof IKnmForm, !!nextReferenceObject?.isAddressHazard);
    },
    [findReferenceObjectByGuid, formRef, name],
  );

  return (
    <KnmCollapse
      id={id}
      theme="fields"
      title={isNew ? COLLAPSE_TITLE_BASE : `${COLLAPSE_TITLE_BASE} №${index + 1}`}
      subtitle={isUse4136 ? value?.actAddress || value?.manualAddress || value.object?.label : value.object?.label}
      isNew={isNew}
      openByDefault={isNew}
      opened={resultsOpenedFlags[id]}
      onClose={onRemove}
      onToggle={handleResultCollapseToggle}
    >
      <>
        <Row>
          <FieldBlock label="Объект проведения КНМ" placementLabel="left" labelSize="col-3">
            <SelectInputField
              className="shared-row-field"
              name={`${name}.object`}
              options={customKnmObjectsSelectOptions}
              disabled={watchOnly}
              size="medium"
              onChange={handleObjectChange}
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock
            label="Дата и время составления акта о проведении КНМ"
            placementLabel="left"
            labelSize="col-3"
            color="black"
            type="not-space"
          >
            <DatePickerField
              className={cn('shared-row-field', styles.DatePickerField)}
              name={`${name}.actCreateDate`}
              timeFormat
              disabled={watchOnly}
              minDate={new Date('01-01-2014')}
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock
            label="Место составления акта о проведении КНМ"
            placementLabel="left"
            labelSize="col-3"
            color="black"
            type="not-space"
          >
            <KnmAddressField
              className={isUseManualAddressAndFiasRegion ? styles.erpKnmAddressField : undefined}
              resize="none"
              formRef={formRef}
              prefixNameNotInFias={isUseManualAddressAndFiasRegion ? `${name}.` : undefined}
              prefixNameMatchesObjectAddress={isUseManualAddressAndFiasRegion ? `${name}.` : undefined}
              manualAddressPlaceholder={isUseManualAddressAndFiasRegion ? 'Место' : 'Адрес'}
              type="row-field"
              id={`${name}.actAddress`}
              addressFieldPathWithPrefix={`${name}.actAddress`}
              required
              disabled={watchOnly}
              trigger={isTrigger}
            />
          </FieldBlock>
        </Row>

        <Row>
          {isReplaceAddressTypeFieldOnHazardCheckBox ? (
            <FieldBlock>
              <CheckboxField
                className={styles.isAddressHazard}
                theme="system"
                size="medium"
                type="checkbox"
                id={`${name}.isAddressHazard`}
                name={`${name}.isAddressHazard`}
                disabled={watchOnly || !!referenceObject}
                label={'Является ОПО'}
              />
            </FieldBlock>
          ) : (
            <FieldBlock label="Тип места" placementLabel="left" labelSize="col-3" color="black" type="not-space">
              <SelectInputField
                className="shared-row-field"
                name={`${name}.actAddressType`}
                options={knmAddressTypesOptions}
                disabled={watchOnly}
                size="medium"
              />
            </FieldBlock>
          )}
        </Row>

        <Row>
          <FieldBlock
            label="Дата и время проведения КНМ"
            placementLabel="left"
            labelSize="col-3"
            color="black"
            type="not-space"
          >
            <DatePickerField
              className={cn('shared-row-field', styles.DatePickerField)}
              name={`${name}.startDate`}
              timeFormat
              disabled={watchOnly}
              minDate={new Date('01-01-2014')}
            />
          </FieldBlock>
        </Row>
        <Row title="Продолжительность проведения КНМ">
          <FieldBlock label="Дней" placementLabel="left" color="black" labelSize="col-3" size="half">
            <TextInputField className="shared-row-field" name={`${name}.durationDays`} disabled={watchOnly} />
          </FieldBlock>
          <FieldBlock label="Часов" placementLabel="left" color="black" labelSize="col-3" size="half">
            <TextInputField className="shared-row-field" name={`${name}.durationHours`} disabled={watchOnly} />
          </FieldBlock>
        </Row>

        <FieldBlock
          label="Фамилия, имя, отчество (последнее - при наличии) и должность должностного лица (должностных лиц),
          проводившего КНМ"
          button={
            <Button
              className="shared-btn-add"
              type="button"
              variant="link"
              color="primary"
              text="Добавить"
              disabled={watchOnly}
              onClick={addInspector}
            />
          }
        >
          <FieldArray name={`${name}.inspectors`}>
            {({ fields }) => {
              if (!fields.length) {
                return null;
              }

              return fields.map((inspectorsFieldName, inspectorIndex) => (
                <Row isListItem margin="medium" notWrap key={inspectorsFieldName}>
                  <Row>
                    <FieldBlock size="col-3">
                      <TextareaField name={`${inspectorsFieldName}.fullName`} placeholder="ФИО" disabled={watchOnly} />
                    </FieldBlock>
                    <FieldBlock size="col-3">
                      <TextareaField
                        name={`${inspectorsFieldName}.position`}
                        placeholder="Должность"
                        disabled={watchOnly}
                      />
                    </FieldBlock>
                    <FieldBlock size="col-3">
                      <SelectInputField
                        name={`${inspectorsFieldName}.type`}
                        placeholder="Тип представителя"
                        options={knmInspectorTypesSelectOptions}
                        isClearable
                        disabled={watchOnly}
                        size="medium"
                      />
                    </FieldBlock>
                  </Row>
                  <CloseButton
                    className="shared-deleting-row-btn"
                    onClick={makeRemoveInspectorHandler(inspectorIndex)}
                  />
                </Row>
              ));
            }}
          </FieldArray>
        </FieldBlock>
        {isUse4136 && (
          <Row>
            <FieldBlock label="Наименование проверяемого лица" id={ORGANIZATION_NAME_BLOCK_ID} color="black">
              <TextInputField inputClassName={styles.OrganizationNameInput} name="organizationName" disabled={true} />
            </FieldBlock>
          </Row>
        )}

        <FieldBlock
          label="Фамилия, имя, отчество (последнее - при наличии) и должность руководителя, иного должностного лица
          юридического лица, уполномоченного представителя юридического лица, уполномоченного представителя
          индивидуального предпринимателя, присутствовавших при проведении КНМ"
          button={
            <Button
              className="shared-btn-add"
              type="button"
              variant="link"
              color="primary"
              text="Добавить"
              disabled={watchOnly}
              onClick={addDelegate}
            />
          }
        >
          <FieldArray name={`${name}.delegates`}>
            {({ fields }) => {
              if (!fields.length) {
                return null;
              }

              return fields.map((delegatesFieldName, delegateIndex) => (
                <Row isListItem margin="medium" notWrap key={delegatesFieldName}>
                  <Row>
                    <FieldBlock size="col-3">
                      <TextareaField name={`${delegatesFieldName}.fullName`} placeholder="ФИО" disabled={watchOnly} />
                    </FieldBlock>
                    <FieldBlock size="col-3">
                      <TextareaField
                        name={`${delegatesFieldName}.position`}
                        placeholder="Должность"
                        disabled={watchOnly}
                      />
                    </FieldBlock>
                    <FieldBlock size="col-3">
                      <SelectInputFieldAutoOptions
                        name={`${delegatesFieldName}.type`}
                        placeholder="Тип представителя"
                        optionName={CommonCatalogsNamesEnum.KNM_DELEGATE_TYPES}
                        isClearable
                        disabled={watchOnly}
                        size="medium"
                      />
                    </FieldBlock>
                  </Row>
                  <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveDelegateHandler(delegateIndex)} />
                </Row>
              ));
            }}
          </FieldArray>
        </FieldBlock>

        <FieldBlock
          label="Сведения о результатах проведения КНМ"
          button={
            <Button
              className="shared-btn-add"
              type="button"
              variant="link"
              color="primary"
              text="Добавить"
              disabled={watchOnly}
              onClick={addAdditionalInfo}
            />
          }
        >
          <FieldArray name={`${name}.additionalInfo`}>
            {({ fields }) => {
              if (!fields.length) {
                return null;
              }

              return fields.map((additionalInfoFieldName, additionalInfoFieldIndex) => {
                const typeSelectValue = objectsResults?.[index]?.additionalInfo?.[additionalInfoFieldIndex]?.type;
                const customKnmResultInfoTypesSelectOptions: IKnmResultInfoTypeSelectOption[] =
                  knmResultInfoTypesSelectOptions?.map(knmResultInfoTypesSelectOption => ({
                    ...knmResultInfoTypesSelectOption,
                    isDisabled: objectsResults?.[index]?.additionalInfo?.some(
                      additionalInfoItem =>
                        additionalInfoItem?.type?.code === knmResultInfoTypesSelectOption.code &&
                        knmResultInfoTypesSelectOption.code !== typeSelectValue?.code,
                    ),
                  })) || [];

                return (
                  <Row isListItem margin="medium" notWrap key={additionalInfoFieldName}>
                    <Row>
                      <Row>
                        <FieldBlock size="half">
                          <SelectInputField
                            name={`${additionalInfoFieldName}.type`}
                            placeholder="Выберите сведения о результатах проведения КНМ"
                            options={customKnmResultInfoTypesSelectOptions}
                            isClearable
                            disabled={watchOnly}
                            size="medium"
                          />
                        </FieldBlock>
                        <FieldBlock size="half">
                          <TextareaField
                            name={`${additionalInfoFieldName}.information`}
                            placeholder="Введите сведения о результатах проведения КНМ"
                            disabled={watchOnly}
                            validate={makeRequired('Поле не может быть пустым')}
                            triggerCalculateHeight={isTrigger}
                          />
                        </FieldBlock>
                      </Row>
                      {objectsResults?.[index]?.additionalInfo?.[additionalInfoFieldIndex]?.type?.code ===
                        KnmResultInfoTypeCodesEnum.ACT_WAS_NOT_READ_NOTE && (
                        <CheckboxField
                          className={styles.ActWasNotReadField}
                          id={`${additionalInfoFieldName}.actWasNotRead`}
                          name={`${additionalInfoFieldName}.actWasNotRead`}
                          label="Отказ от ознакомления"
                          size="medium"
                          type="checkbox"
                          theme="system"
                          disabled={watchOnly}
                        />
                      )}
                    </Row>
                    <CloseButton
                      className="shared-deleting-row-btn"
                      onClick={makeRemoveAdditionalInfoHandler(additionalInfoFieldIndex)}
                    />
                  </Row>
                );
              });
            }}
          </FieldArray>
        </FieldBlock>
      </>
    </KnmCollapse>
  );
};
